import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { 
  ArrowLeft, 
  FileText, 
  Download, 
  Calendar, 
  MapPin, 
  Briefcase, 
  GraduationCap, 
  Languages, 
  Wrench, 
  Users, 
  Mail, 
  Phone,
  Building,
  User,
  FileDown,
  Edit,
  Save,
  X,
  Plus,
  Trash2,
  Upload
} from 'lucide-react'

interface Application {
  id: string
  job_id: string | null
  job_title: string | null
  first_name: string
  last_name: string
  email: string
  phone: string
  city: string
  country: string | null
  education: Array<{
    _school: string
    _diploma: string
    _year: string
  }>
  languages: string[]
  additional_languages: string
  technical_skills: string[]
  additional_tech_skills: string
  tools: string[]
  additional_tools: string
  soft_skills: string[]
  additional_soft_skills: string
  references: Array<{
    _first_name: string
    _last_name: string
    _email: string
    _company: string
    _phone: string
  }>
  experiences: Array<{
    _company: string
    _role: string
    _start_year: string
    _end_year: string
  }>
  cv_url: string | null
  cover_letter_url: string | null
  diplomas_url: string | null
  work_certificates_url: string | null
  created_at: string | null
  modified_at: string | null
}

interface TaxonomyTerm {
  id: number
  name: string
}

export default function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [application, setApplication] = useState<Application | null>(null)
  const [editData, setEditData] = useState<Application | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  
  // Taxonomy data
  const [languages, setLanguages] = useState<TaxonomyTerm[]>([])
  const [technicalSkills, setTechnicalSkills] = useState<TaxonomyTerm[]>([])
  const [tools, setTools] = useState<TaxonomyTerm[]>([])
  const [softSkills, setSoftSkills] = useState<TaxonomyTerm[]>([])
  const [countries, setCountries] = useState<TaxonomyTerm[]>([])
  
  // New language dialog
  const [showAddLanguage, setShowAddLanguage] = useState(false)
  const [newLanguageName, setNewLanguageName] = useState('')
  const [newLanguageProficiency, setNewLanguageProficiency] = useState('Beginner')
  
  // Generic taxonomy dialog
  const [showAddTaxonomy, setShowAddTaxonomy] = useState(false)
  const [addTaxonomyType, setAddTaxonomyType] = useState<'technical-skills' | 'tools' | 'soft-skills'>('technical-skills')
  const [newTaxonomyName, setNewTaxonomyName] = useState('')
  
  // Dropdown open states
  const [countryOpen, setCountryOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const [techSkillOpen, setTechSkillOpen] = useState(false)
  const [toolOpen, setToolOpen] = useState(false)
  const [softSkillOpen, setSoftSkillOpen] = useState(false)
  
  // Search filter states
  const [countrySearch, setCountrySearch] = useState('')
  const [languageSearch, setLanguageSearch] = useState('')
  const [techSkillSearch, setTechSkillSearch] = useState('')
  const [toolSearch, setToolSearch] = useState('')
  const [softSkillSearch, setSoftSkillSearch] = useState('')
  
  // File upload state
  const [uploadingFile, setUploadingFile] = useState<string | null>(null)

  useEffect(() => {
    fetchApplication()
    fetchTaxonomies()
  }, [id])

  const fetchTaxonomies = async () => {
    try {
      const config = (window as any).WP_COCKPIT
      if (!config) return

      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/applications/taxonomies`
      const headers: Record<string, string> = {}
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers
      })

      const data = await response.json()
      if (response.ok && data.data) {
        setLanguages(data.data.languages || [])
        setTechnicalSkills(data.data.technical_skills || [])
        setTools(data.data.tools || [])
        setSoftSkills(data.data.soft_skills || [])
        setCountries(data.data.countries || [])
      }
    } catch (err) {
      console.error('Error fetching taxonomies:', err)
    }
  }

  const fetchApplication = async () => {
    try {
      setLoading(true)
      setError(null)

      const config = (window as any).WP_COCKPIT
      if (!config) throw new Error('WP Cockpit config not available')

      // Add timestamp to prevent caching
      const timestamp = new Date().getTime()
      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/applications?_=${timestamp}`
      const headers: Record<string, string> = {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch application')
      }

      const apps = data.data || []
      const app = apps.find((a: Application) => a.id === id)

      if (!app) {
        throw new Error('Application not found')
      }

      setApplication(app)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load application')
      console.error('Error fetching application:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const parseMultipleUrls = (urlString: string | null): string[] => {
    if (!urlString) return []
    return urlString.split(',').map(url => url.trim()).filter(Boolean)
  }

  const mergeSkills = (skills: string[], additionalSkills: string): string[] => {
    const merged = [...skills]
    if (additionalSkills && additionalSkills.trim()) {
      const additional = additionalSkills.split(',').map(s => s.trim()).filter(Boolean)
      merged.push(...additional)
    }
    return merged
  }

  const getFileExtension = (url: string): string => {
    try {
      const pathname = new URL(url).pathname
      const ext = pathname.split('.').pop()?.toLowerCase() || ''
      return ext
    } catch {
      return ''
    }
  }

  const isImage = (url: string): boolean => {
    const ext = getFileExtension(url)
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)
  }

  const isPDF = (url: string): boolean => {
    const ext = getFileExtension(url)
    return ext === 'pdf'
  }

  const downloadAllDocuments = () => {
    const allUrls = [
      application?.cv_url,
      application?.cover_letter_url,
      ...diplomaUrls,
      ...certificateUrls
    ].filter(Boolean) as string[]

    allUrls.forEach((url, index) => {
      setTimeout(() => {
        const link = document.createElement('a')
        link.href = url
        link.download = ''
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }, index * 200)
    })
  }

  const handleEdit = () => {
    if (application) {
      setEditData(JSON.parse(JSON.stringify(application)))
      setIsEditing(true)
    }
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditData(null)
  }

  const handleSave = async () => {
    const wpConfig = (window as any).WP_COCKPIT
    const isDev = wpConfig?.devMode || false
    
    if (isDev) {
      console.log('=== SAVE BUTTON CLICKED ===')
    }
    
    if (!editData) {
      console.error('No editData available')
      return
    }

    try {
      setSaving(true)
      const config = (window as any).WP_COCKPIT
      if (!config) throw new Error('WP Cockpit config not available')

      if (isDev) {
        console.log('Starting save...', { editData })
      }

      // Convert language/skill names back to IDs
      const languageIds = editData.languages.map(name => 
        languages.find(l => l.name === name)?.id
      ).filter(Boolean)
      
      const techSkillIds = editData.technical_skills.map(name =>
        technicalSkills.find(s => s.name === name)?.id
      ).filter(Boolean)
      
      const toolIds = editData.tools.map(name =>
        tools.find(t => t.name === name)?.id
      ).filter(Boolean)
      
      const softSkillIds = editData.soft_skills.map(name =>
        softSkills.find(s => s.name === name)?.id
      ).filter(Boolean)

      const countryId = countries.find(c => c.name === editData.country)?.id

      if (isDev) {
        console.log('Converted IDs:', { languageIds, techSkillIds, toolIds, softSkillIds, countryId })
      }

      const updateData = {
        first_name: editData.first_name,
        last_name: editData.last_name,
        phone: editData.phone,
        city: editData.city,
        country: countryId,
        education: editData.education,
        experiences: editData.experiences,
        references: editData.references,
        languages: languageIds,
        technical_skills: techSkillIds,
        tools: toolIds,
        soft_skills: softSkillIds,
        additional_languages: editData.additional_languages,
        additional_tech_skills: editData.additional_tech_skills,
        additional_tools: editData.additional_tools,
        additional_soft_skills: editData.additional_soft_skills,
        cv_url: editData.cv_url,
        cover_letter_url: editData.cover_letter_url,
        diplomas_url: editData.diplomas_url,
        work_certificates_url: editData.work_certificates_url
      }

      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/applications/${id}`
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      }
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce

      if (isDev) {
        console.log('Sending update:', { url, updateData })
      }

      const response = await fetch(url, {
        method: 'PUT',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify(updateData)
      })

      const data = await response.json()
      if (isDev) {
        console.log('Response:', { ok: response.ok, status: response.status, data })
      }

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update application')
      }

      toast({
        title: 'Success',
        description: 'Application updated successfully'
      })

      // Refresh data and exit edit mode
      await fetchApplication()
      setIsEditing(false)
      setEditData(null)
    } catch (err) {
      console.error('=== SAVE ERROR ===' , err)
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update application'
      })
    } finally {
      setSaving(false)
    }
  }

  const handleFileUpload = async (fieldName: string, file: File) => {
    try {
      setUploadingFile(fieldName)
      
      const formData = new FormData()
      formData.append('file', file)

      const config = (window as any).WP_COCKPIT
      const headers: Record<string, string> = {}
      if (config?.restNonce) headers['X-WP-Nonce'] = config.restNonce

      const response = await fetch('/wp-json/wp/v2/media', {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: formData
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload file')
      }

      if (editData) {
        const newEditData = { ...editData }
        if (fieldName === 'diplomas_url' || fieldName === 'work_certificates_url') {
          const currentUrls = newEditData[fieldName] ? newEditData[fieldName]!.split(',').map(u => u.trim()) : []
          currentUrls.push(data.source_url)
          newEditData[fieldName] = currentUrls.join(',')
        } else {
          newEditData[fieldName as keyof Application] = data.source_url as any
        }
        setEditData(newEditData)
      }

      toast({
        title: 'Success',
        description: 'File uploaded successfully'
      })
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to upload file'
      })
    } finally {
      setUploadingFile(null)
    }
  }

  const handleRemoveDocument = (fieldName: string, urlToRemove?: string) => {
    if (!editData) return
    
    const newEditData = { ...editData }
    if (fieldName === 'diplomas_url' || fieldName === 'work_certificates_url') {
      const currentUrls = newEditData[fieldName] ? newEditData[fieldName]!.split(',').map(u => u.trim()) : []
      const filtered = urlToRemove ? currentUrls.filter(u => u !== urlToRemove) : []
      newEditData[fieldName] = filtered.join(',')
    } else {
      newEditData[fieldName as keyof Application] = null as any
    }
    setEditData(newEditData)
  }

  const addNewLanguage = async () => {
    if (!newLanguageName.trim()) return

    try {
      const config = (window as any).WP_COCKPIT
      const languageWithProficiency = `${newLanguageName.trim()} (${newLanguageProficiency})`
      
      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/applications/taxonomy/languages`
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      }
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({ name: languageWithProficiency })
      })

      const data = await response.json()

      if (response.ok && data.data) {
        setLanguages([...languages, data.data])
        if (editData) {
          setEditData({
            ...editData,
            languages: [...editData.languages, data.data.name]
          })
        }
        setShowAddLanguage(false)
        setNewLanguageName('')
        setNewLanguageProficiency('Beginner')
        
        toast({
          title: 'Success',
          description: 'Language added successfully'
        })
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to add language'
      })
    }
  }

  const addNewTaxonomyTerm = async () => {
    if (!newTaxonomyName.trim()) return

    try {
      const config = (window as any).WP_COCKPIT
      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/applications/taxonomy/${addTaxonomyType}`
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      }
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({ name: newTaxonomyName.trim() })
      })

      const data = await response.json()

      if (response.ok && data.data) {
        // Update the appropriate taxonomy list
        if (addTaxonomyType === 'technical-skills') {
          setTechnicalSkills([...technicalSkills, data.data])
          if (editData) {
            setEditData({
              ...editData,
              technical_skills: [...editData.technical_skills, data.data.name]
            })
          }
        } else if (addTaxonomyType === 'tools') {
          setTools([...tools, data.data])
          if (editData) {
            setEditData({
              ...editData,
              tools: [...editData.tools, data.data.name]
            })
          }
        } else if (addTaxonomyType === 'soft-skills') {
          setSoftSkills([...softSkills, data.data])
          if (editData) {
            setEditData({
              ...editData,
              soft_skills: [...editData.soft_skills, data.data.name]
            })
          }
        }
        
        setShowAddTaxonomy(false)
        setNewTaxonomyName('')
        
        toast({
          title: 'Success',
          description: `${addTaxonomyType.replace('-', ' ')} added successfully`
        })
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to add term'
      })
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Spinner className="h-8 w-8" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !application) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Alert variant="destructive">
            <AlertDescription>{error || 'Application not found'}</AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  const allLanguages = mergeSkills(application.languages, application.additional_languages)
  const allTechSkills = mergeSkills(application.technical_skills, application.additional_tech_skills)
  const allTools = mergeSkills(application.tools, application.additional_tools)
  const allSoftSkills = mergeSkills(application.soft_skills, application.additional_soft_skills)
  const diplomaUrls = parseMultipleUrls(application.diplomas_url)
  const certificateUrls = parseMultipleUrls(application.work_certificates_url)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          {!isEditing ? (
            <Button onClick={handleEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Application
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancelEdit} disabled={saving}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Spinner className="h-4 w-4 mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Header Card */}
        <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-2xl">
            Application for {application.job_title || 'Job Position'}
          </CardTitle>
          <CardDescription className="space-y-2">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Applied on {formatDate(application.created_at)}</span>
            </div>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-3">Applicant Information</h3>
              {!isEditing || !editData ? (
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{application.first_name} {application.last_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{application.email}</span>
                  </div>
                  {application.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{application.phone}</span>
                    </div>
                  )}
                  {application.city && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{application.city}{application.country && `, ${application.country}`}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={editData.first_name}
                      onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={editData.last_name}
                      onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={editData.phone}
                      onChange={(e) => setEditData({...editData, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={editData.city}
                      onChange={(e) => setEditData({...editData, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <div className="space-y-2">
                      {/* Display selected country as badge */}
                      {editData.country && (
                        <div className="flex gap-2">
                          <Badge variant="secondary" className="pl-2 pr-1">
                            {editData.country}
                            <button
                              onClick={() => {
                                setEditData({...editData, country: ''})
                              }}
                              className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        </div>
                      )}
                      
                      {/* Search field for selecting country */}
                      <div className="relative">
                        <Input
                          placeholder="Search country..."
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          onFocus={() => setCountryOpen(true)}
                        />
                        {countryOpen && (
                          <>
                            <div 
                              className="fixed inset-0 z-10" 
                              onClick={() => {
                                setCountryOpen(false)
                                setCountrySearch('')
                              }}
                            />
                            <div className="absolute z-20 w-full mt-1 border rounded-md shadow-lg bg-popover max-h-[300px] overflow-y-auto">
                              {countries
                                .filter(country => 
                                  country.name.toLowerCase().includes(countrySearch.toLowerCase())
                                )
                                .map((country) => (
                                <div
                                  key={country.id}
                                  className="p-2 hover:bg-accent cursor-pointer border-b last:border-b-0"
                                  onClick={() => {
                                    setEditData({...editData, country: country.name})
                                    setCountrySearch('')
                                    setCountryOpen(false)
                                  }}
                                >
                                  <span className="text-sm">{country.name}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Experience */}
        {(!isEditing && application.experiences && application.experiences.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Work Experience
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {application.experiences.map((exp, idx) => (
                <div key={idx} className="border-l-2 border-primary pl-4">
                  <h4 className="font-medium">{exp._role}</h4>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Building className="h-3 w-3" />
                    <span>{exp._company}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {exp._start_year} - {exp._end_year}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        
        {/* Experience - Edit Mode */}
        {isEditing && editData && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Work Experience
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newExperiences = [...editData.experiences, { _role: '', _company: '', _start_year: '', _end_year: '' }]
                    setEditData({ ...editData, experiences: newExperiences })
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Experience
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editData.experiences.map((exp, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Experience {idx + 1}</span>
                    {editData.experiences.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newExperiences = editData.experiences.filter((_, i) => i !== idx)
                          setEditData({ ...editData, experiences: newExperiences })
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid gap-3">
                    <div>
                      <Label htmlFor={`exp-role-${idx}`}>Role *</Label>
                      <Input
                        id={`exp-role-${idx}`}
                        value={exp._role}
                        onChange={(e) => {
                          const newExperiences = [...editData.experiences]
                          newExperiences[idx] = { ...exp, _role: e.target.value }
                          setEditData({ ...editData, experiences: newExperiences })
                        }}
                        placeholder="e.g. Senior Developer"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`exp-company-${idx}`}>Company *</Label>
                      <Input
                        id={`exp-company-${idx}`}
                        value={exp._company}
                        onChange={(e) => {
                          const newExperiences = [...editData.experiences]
                          newExperiences[idx] = { ...exp, _company: e.target.value }
                          setEditData({ ...editData, experiences: newExperiences })
                        }}
                        placeholder="e.g. Tech Corp"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor={`exp-start-${idx}`}>Start Year *</Label>
                        <Input
                          id={`exp-start-${idx}`}
                          value={exp._start_year}
                          onChange={(e) => {
                            const newExperiences = [...editData.experiences]
                            newExperiences[idx] = { ...exp, _start_year: e.target.value }
                            setEditData({ ...editData, experiences: newExperiences })
                          }}
                          placeholder="2020"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`exp-end-${idx}`}>End Year *</Label>
                        <Input
                          id={`exp-end-${idx}`}
                          value={exp._end_year}
                          onChange={(e) => {
                            const newExperiences = [...editData.experiences]
                            newExperiences[idx] = { ...exp, _end_year: e.target.value }
                            setEditData({ ...editData, experiences: newExperiences })
                          }}
                          placeholder="Present"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Education */}
        {(!isEditing && application.education && application.education.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Education
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {application.education.map((edu, idx) => (
                <div key={idx} className="border-l-2 border-primary pl-4">
                  <h4 className="font-medium">{edu._diploma}</h4>
                  <p className="text-sm text-muted-foreground">{edu._school}</p>
                  <p className="text-sm text-muted-foreground mt-1">{edu._year}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        
        {/* Education - Edit Mode */}
        {isEditing && editData && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  Education
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newEducation = [...editData.education, { _diploma: '', _school: '', _year: '' }]
                    setEditData({ ...editData, education: newEducation })
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Education
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editData.education.map((edu, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Education {idx + 1}</span>
                    {editData.education.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newEducation = editData.education.filter((_, i) => i !== idx)
                          setEditData({ ...editData, education: newEducation })
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid gap-3">
                    <div>
                      <Label htmlFor={`edu-diploma-${idx}`}>Diploma/Degree *</Label>
                      <Input
                        id={`edu-diploma-${idx}`}
                        value={edu._diploma}
                        onChange={(e) => {
                          const newEducation = [...editData.education]
                          newEducation[idx] = { ...edu, _diploma: e.target.value }
                          setEditData({ ...editData, education: newEducation })
                        }}
                        placeholder="e.g. Bachelor of Science"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`edu-school-${idx}`}>School/University *</Label>
                      <Input
                        id={`edu-school-${idx}`}
                        value={edu._school}
                        onChange={(e) => {
                          const newEducation = [...editData.education]
                          newEducation[idx] = { ...edu, _school: e.target.value }
                          setEditData({ ...editData, education: newEducation })
                        }}
                        placeholder="e.g. University of XYZ"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`edu-year-${idx}`}>Graduation Year *</Label>
                      <Input
                        id={`edu-year-${idx}`}
                        value={edu._year}
                        onChange={(e) => {
                          const newEducation = [...editData.education]
                          newEducation[idx] = { ...edu, _year: e.target.value }
                          setEditData({ ...editData, education: newEducation })
                        }}
                        placeholder="2020"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Languages */}
        {(!isEditing && allLanguages.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Languages className="h-5 w-5" />
                Languages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {allLanguages.map((lang, idx) => (
                  <Badge key={idx} variant="secondary">
                    {lang}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Languages - Edit Mode */}
        {isEditing && editData && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Languages className="h-5 w-5" />
                  Languages
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddLanguage(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Language
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Selected Languages as Badges */}
              {editData.languages.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {editData.languages.map((lang, idx) => (
                    <Badge key={idx} variant="secondary" className="pl-2 pr-1">
                      {lang}
                      <button
                        onClick={() => {
                          setEditData({
                            ...editData,
                            languages: editData.languages.filter(l => l !== lang)
                          })
                        }}
                        className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Search and Select */}
              <div className="relative">
                <Input
                  placeholder="Search languages..."
                  value={languageSearch}
                  onChange={(e) => setLanguageSearch(e.target.value)}
                  onFocus={() => setLanguageOpen(true)}
                />
                {languageOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setLanguageOpen(false)}
                    />
                    <div className="absolute z-20 w-full mt-1 border rounded-md shadow-lg bg-popover max-h-[300px] overflow-y-auto">
                      {languages
                        .filter(lang => 
                          lang.name.toLowerCase().includes(languageSearch.toLowerCase())
                        )
                        .map((lang) => (
                        <label
                          key={lang.id}
                          className="flex items-center gap-2 p-2 hover:bg-accent cursor-pointer border-b last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={editData.languages.includes(lang.name)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEditData({
                                  ...editData,
                                  languages: [...editData.languages, lang.name]
                                })
                              } else {
                                setEditData({
                                  ...editData,
                                  languages: editData.languages.filter(l => l !== lang.name)
                                })
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{lang.name}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Technical Skills */}
        {(!isEditing && allTechSkills.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Technical Skills
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {allTechSkills.map((skill, idx) => (
                  <Badge key={idx} variant="secondary">
                    {skill}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Technical Skills - Edit Mode */}
        {isEditing && editData && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="h-5 w-5" />
                  Technical Skills
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAddTaxonomyType('technical-skills')
                    setShowAddTaxonomy(true)
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Skill
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Selected Skills as Badges */}
              {editData.technical_skills.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {editData.technical_skills.map((skill, idx) => (
                    <Badge key={idx} variant="secondary" className="pl-2 pr-1">
                      {skill}
                      <button
                        onClick={() => {
                          setEditData({
                            ...editData,
                            technical_skills: editData.technical_skills.filter(s => s !== skill)
                          })
                        }}
                        className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Search and Select */}
              <div className="relative">
                <Input
                  placeholder="Search skills..."
                  value={techSkillSearch}
                  onChange={(e) => setTechSkillSearch(e.target.value)}
                  onFocus={() => setTechSkillOpen(true)}
                />
                {techSkillOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setTechSkillOpen(false)}
                    />
                    <div className="absolute z-20 w-full mt-1 border rounded-md shadow-lg bg-popover max-h-[300px] overflow-y-auto">
                      {technicalSkills
                        .filter(skill => 
                          skill.name.toLowerCase().includes(techSkillSearch.toLowerCase())
                        )
                        .map((skill) => (
                        <label
                          key={skill.id}
                          className="flex items-center gap-2 p-2 hover:bg-accent cursor-pointer border-b last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={editData.technical_skills.includes(skill.name)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEditData({
                                  ...editData,
                                  technical_skills: [...editData.technical_skills, skill.name]
                                })
                              } else {
                                setEditData({
                                  ...editData,
                                  technical_skills: editData.technical_skills.filter(s => s !== skill.name)
                                })
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{skill.name}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tools */}
        {(!isEditing && allTools.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Tools
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {allTools.map((tool, idx) => (
                  <Badge key={idx} variant="outline">
                    {tool}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tools - Edit Mode */}
        {isEditing && editData && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="h-5 w-5" />
                  Tools
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAddTaxonomyType('tools')
                    setShowAddTaxonomy(true)
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Tool
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Selected Tools as Badges */}
              {editData.tools.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {editData.tools.map((tool, idx) => (
                    <Badge key={idx} variant="outline" className="pl-2 pr-1">
                      {tool}
                      <button
                        onClick={() => {
                          setEditData({
                            ...editData,
                            tools: editData.tools.filter(t => t !== tool)
                          })
                        }}
                        className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Search and Select */}
              <div className="relative">
                <Input
                  placeholder="Search tools..."
                  value={toolSearch}
                  onChange={(e) => setToolSearch(e.target.value)}
                  onFocus={() => setToolOpen(true)}
                />
                {toolOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setToolOpen(false)}
                    />
                    <div className="absolute z-20 w-full mt-1 border rounded-md shadow-lg bg-popover max-h-[300px] overflow-y-auto">
                      {tools
                        .filter(tool => 
                          tool.name.toLowerCase().includes(toolSearch.toLowerCase())
                        )
                        .map((tool) => (
                        <label
                          key={tool.id}
                          className="flex items-center gap-2 p-2 hover:bg-accent cursor-pointer border-b last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={editData.tools.includes(tool.name)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEditData({
                                  ...editData,
                                  tools: [...editData.tools, tool.name]
                                })
                              } else {
                                setEditData({
                                  ...editData,
                                  tools: editData.tools.filter(t => t !== tool.name)
                                })
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{tool.name}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Soft Skills */}
        {(!isEditing && allSoftSkills.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Soft Skills
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {allSoftSkills.map((skill, idx) => (
                  <Badge key={idx} variant="secondary">
                    {skill}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Soft Skills - Edit Mode */}
        {isEditing && editData && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Soft Skills
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAddTaxonomyType('soft-skills')
                    setShowAddTaxonomy(true)
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Skill
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Selected Skills as Badges */}
              {editData.soft_skills.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {editData.soft_skills.map((skill, idx) => (
                    <Badge key={idx} variant="secondary" className="pl-2 pr-1">
                      {skill}
                      <button
                        onClick={() => {
                          setEditData({
                            ...editData,
                            soft_skills: editData.soft_skills.filter(s => s !== skill)
                          })
                        }}
                        className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Search and Select */}
              <div className="relative">
                <Input
                  placeholder="Search skills..."
                  value={softSkillSearch}
                  onChange={(e) => setSoftSkillSearch(e.target.value)}
                  onFocus={() => setSoftSkillOpen(true)}
                />
                {softSkillOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setSoftSkillOpen(false)}
                    />
                    <div className="absolute z-20 w-full mt-1 border rounded-md shadow-lg bg-popover max-h-[300px] overflow-y-auto">
                      {softSkills
                        .filter(skill => 
                          skill.name.toLowerCase().includes(softSkillSearch.toLowerCase())
                        )
                        .map((skill) => (
                        <label
                          key={skill.id}
                          className="flex items-center gap-2 p-2 hover:bg-accent cursor-pointer border-b last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={editData.soft_skills.includes(skill.name)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEditData({
                                  ...editData,
                                  soft_skills: [...editData.soft_skills, skill.name]
                                })
                              } else {
                                setEditData({
                                  ...editData,
                                  soft_skills: editData.soft_skills.filter(s => s !== skill.name)
                                })
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{skill.name}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* References */}
        {(!isEditing && application.references && application.references.length > 0) && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                References
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {application.references.map((ref, idx) => (
                  <div key={idx} className="border rounded-lg p-4">
                    <h4 className="font-medium">{ref._first_name} {ref._last_name}</h4>
                    <p className="text-sm text-muted-foreground">{ref._company}</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        <span className="text-muted-foreground">{ref._email}</span>
                      </div>
                      {ref._phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          <span className="text-muted-foreground">{ref._phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* References - Edit Mode */}
        {isEditing && editData && (
          <Card className="md:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  References
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newReferences = [...editData.references, { _first_name: '', _last_name: '', _company: '', _email: '', _phone: '' }]
                    setEditData({ ...editData, references: newReferences })
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Reference
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editData.references.map((ref, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Reference {idx + 1}</span>
                    {editData.references.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newReferences = editData.references.filter((_, i) => i !== idx)
                          setEditData({ ...editData, references: newReferences })
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label htmlFor={`ref-first-${idx}`}>First Name *</Label>
                      <Input
                        id={`ref-first-${idx}`}
                        value={ref._first_name}
                        onChange={(e) => {
                          const newReferences = [...editData.references]
                          newReferences[idx] = { ...ref, _first_name: e.target.value }
                          setEditData({ ...editData, references: newReferences })
                        }}
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`ref-last-${idx}`}>Last Name *</Label>
                      <Input
                        id={`ref-last-${idx}`}
                        value={ref._last_name}
                        onChange={(e) => {
                          const newReferences = [...editData.references]
                          newReferences[idx] = { ...ref, _last_name: e.target.value }
                          setEditData({ ...editData, references: newReferences })
                        }}
                        placeholder="Doe"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`ref-company-${idx}`}>Company *</Label>
                      <Input
                        id={`ref-company-${idx}`}
                        value={ref._company}
                        onChange={(e) => {
                          const newReferences = [...editData.references]
                          newReferences[idx] = { ...ref, _company: e.target.value }
                          setEditData({ ...editData, references: newReferences })
                        }}
                        placeholder="Tech Corp"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`ref-email-${idx}`}>Email *</Label>
                      <Input
                        id={`ref-email-${idx}`}
                        type="email"
                        value={ref._email}
                        onChange={(e) => {
                          const newReferences = [...editData.references]
                          newReferences[idx] = { ...ref, _email: e.target.value }
                          setEditData({ ...editData, references: newReferences })
                        }}
                        placeholder="john@example.com"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor={`ref-phone-${idx}`}>Phone (Optional)</Label>
                      <Input
                        id={`ref-phone-${idx}`}
                        value={ref._phone || ''}
                        onChange={(e) => {
                          const newReferences = [...editData.references]
                          newReferences[idx] = { ...ref, _phone: e.target.value }
                          setEditData({ ...editData, references: newReferences })
                        }}
                        placeholder="+1234567890"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Documents - View Mode */}
        {!isEditing && (
        <Card className="md:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Documents
              </CardTitle>
              {(application.cv_url || application.cover_letter_url || diplomaUrls.length > 0 || certificateUrls.length > 0) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadAllDocuments}
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  Download All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* CV and Cover Letter in same row */}
            {(application.cv_url || application.cover_letter_url) && (
              <div className="grid gap-4 md:grid-cols-2">
                {/* CV */}
                {application.cv_url && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Curriculum Vitae
                    </h4>
                    <div className="border rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800">
                      {isImage(application.cv_url) ? (
                        <div className="relative group">
                          <img 
                            src={application.cv_url} 
                            alt="CV" 
                            className="w-full h-auto max-h-96 object-contain"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button
                              variant="secondary"
                              onClick={() => window.open(application.cv_url!, '_blank')}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ) : isPDF(application.cv_url) ? (
                        <div className="p-4 space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                              <FileText className="h-6 w-6 text-red-600 dark:text-red-400" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">CV.pdf</p>
                              <p className="text-sm text-muted-foreground">PDF Document</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => window.open(application.cv_url!, '_blank')}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => {
                                const link = document.createElement('a')
                                link.href = application.cv_url!
                                link.download = 'CV.pdf'
                                link.click()
                              }}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4">
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => window.open(application.cv_url!, '_blank')}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download CV
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Cover Letter */}
                {application.cover_letter_url && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Cover Letter
                </h4>
                <div className="border rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800">
                  {isImage(application.cover_letter_url) ? (
                    <div className="relative group">
                      <img 
                        src={application.cover_letter_url} 
                        alt="Cover Letter" 
                        className="w-full h-auto max-h-96 object-contain"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button
                          variant="secondary"
                          onClick={() => window.open(application.cover_letter_url!, '_blank')}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ) : isPDF(application.cover_letter_url) ? (
                    <div className="p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                          <FileText className="h-6 w-6 text-red-600 dark:text-red-400" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">Cover Letter.pdf</p>
                          <p className="text-sm text-muted-foreground">PDF Document</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => window.open(application.cover_letter_url!, '_blank')}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            const link = document.createElement('a')
                            link.href = application.cover_letter_url!
                            link.download = 'Cover-Letter.pdf'
                            link.click()
                          }}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => window.open(application.cover_letter_url!, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download Cover Letter
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>
          )}

            {/* Diplomas */}
            {diplomaUrls.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" />
                  Diplomas ({diplomaUrls.length})
                </h4>
                <div className="grid gap-3 md:grid-cols-2">
                  {diplomaUrls.map((url, idx) => (
                    <div key={`diploma-${idx}`} className="border rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800">
                      {isImage(url) ? (
                        <div className="relative group">
                          <img 
                            src={url} 
                            alt={`Diploma ${idx + 1}`} 
                            className="w-full h-48 object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => window.open(url, '_blank')}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ) : isPDF(url) ? (
                        <div className="p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="h-10 w-10 rounded bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                              <FileText className="h-5 w-5 text-red-600 dark:text-red-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">Diploma {idx + 1}.pdf</p>
                              <p className="text-xs text-muted-foreground">PDF Document</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => window.open(url, '_blank')}
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => {
                                const link = document.createElement('a')
                                link.href = url
                                link.download = `Diploma-${idx + 1}.pdf`
                                link.click()
                              }}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-3">
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => window.open(url, '_blank')}
                          >
                            <Download className="h-3 w-3 mr-2" />
                            Diploma {idx + 1}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Work Certificates */}
            {certificateUrls.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Work Certificates ({certificateUrls.length})
                </h4>
                <div className="grid gap-3 md:grid-cols-2">
                  {certificateUrls.map((url, idx) => (
                    <div key={`cert-${idx}`} className="border rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800">
                      {isImage(url) ? (
                        <div className="relative group">
                          <img 
                            src={url} 
                            alt={`Certificate ${idx + 1}`} 
                            className="w-full h-48 object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => window.open(url, '_blank')}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ) : isPDF(url) ? (
                        <div className="p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="h-10 w-10 rounded bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                              <FileText className="h-5 w-5 text-red-600 dark:text-red-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">Certificate {idx + 1}.pdf</p>
                              <p className="text-xs text-muted-foreground">PDF Document</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => window.open(url, '_blank')}
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => {
                                const link = document.createElement('a')
                                link.href = url
                                link.download = `Certificate-${idx + 1}.pdf`
                                link.click()
                              }}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-3">
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => window.open(url, '_blank')}
                          >
                            <Download className="h-3 w-3 mr-2" />
                            Certificate {idx + 1}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!application.cv_url && !application.cover_letter_url && diplomaUrls.length === 0 && certificateUrls.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No documents uploaded</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}
        
        {/* Documents - Edit Mode */}
        {isEditing && editData && (
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* CV and Cover Letter - Single Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* CV Upload */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Curriculum Vitae *
                </Label>
                {editData.cv_url ? (
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm truncate flex-1">CV uploaded</span>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(editData.cv_url!, '_blank')}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveDocument('cv_url')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    <input
                      type="file"
                      id="cv-upload"
                      className="hidden"
                      accept=".pdf,image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleFileUpload('cv_url', file)
                      }}
                      disabled={uploadingFile === 'cv_url'}
                    />
                    <label htmlFor="cv-upload" className="cursor-pointer">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {uploadingFile === 'cv_url' ? 'Uploading...' : 'Click to upload CV'}
                      </p>
                    </label>
                  </div>
                )}
              </div>

              {/* Cover Letter Upload */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Cover Letter *
                </Label>
                {editData.cover_letter_url ? (
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm truncate flex-1">Cover letter uploaded</span>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(editData.cover_letter_url!, '_blank')}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveDocument('cover_letter_url')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    <input
                      type="file"
                      id="cover-letter-upload"
                      className="hidden"
                      accept=".pdf,image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleFileUpload('cover_letter_url', file)
                      }}
                      disabled={uploadingFile === 'cover_letter_url'}
                    />
                    <label htmlFor="cover-letter-upload" className="cursor-pointer">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {uploadingFile === 'cover_letter_url' ? 'Uploading...' : 'Click to upload cover letter'}
                      </p>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Diplomas */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Diplomas (Optional)
              </Label>
              <div className="space-y-2">
                {editData.diplomas_url && editData.diplomas_url.split(',').map(url => url.trim()).filter(Boolean).map((url, idx) => (
                  <div key={idx} className="border rounded-lg p-3 flex items-center justify-between">
                    <span className="text-sm truncate flex-1">Diploma {idx + 1}</span>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(url, '_blank')}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveDocument('diplomas_url', url)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  <input
                    type="file"
                    id="diploma-upload"
                    className="hidden"
                    accept=".pdf,image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleFileUpload('diplomas_url', file)
                    }}
                    disabled={uploadingFile === 'diplomas_url'}
                  />
                  <label htmlFor="diploma-upload" className="cursor-pointer">
                    <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {uploadingFile === 'diplomas_url' ? 'Uploading...' : 'Click to add diploma'}
                    </p>
                  </label>
                </div>
              </div>
            </div>

            {/* Work Certificates */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Work Certificates (Optional)
              </Label>
              <div className="space-y-2">
                {editData.work_certificates_url && editData.work_certificates_url.split(',').map(url => url.trim()).filter(Boolean).map((url, idx) => (
                  <div key={idx} className="border rounded-lg p-3 flex items-center justify-between">
                    <span className="text-sm truncate flex-1">Certificate {idx + 1}</span>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(url, '_blank')}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveDocument('work_certificates_url', url)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  <input
                    type="file"
                    id="certificate-upload"
                    className="hidden"
                    accept=".pdf,image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleFileUpload('work_certificates_url', file)
                    }}
                    disabled={uploadingFile === 'work_certificates_url'}
                  />
                  <label htmlFor="certificate-upload" className="cursor-pointer">
                    <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {uploadingFile === 'work_certificates_url' ? 'Uploading...' : 'Click to add certificate'}
                    </p>
                  </label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
      </div>
      </div>
      
      {/* Add Language Dialog */}
      <Dialog open={showAddLanguage} onOpenChange={setShowAddLanguage}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Language</DialogTitle>
            <DialogDescription>
              Add a new language with proficiency level to your application
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="language-name">Language Name</Label>
              <Input
                id="language-name"
                value={newLanguageName}
                onChange={(e) => setNewLanguageName(e.target.value)}
                placeholder="e.g. Spanish"
              />
            </div>
            <div>
              <Label htmlFor="language-proficiency">Proficiency</Label>
              <Select value={newLanguageProficiency} onValueChange={setNewLanguageProficiency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Beginner">Beginner</SelectItem>
                  <SelectItem value="Intermediate">Intermediate</SelectItem>
                  <SelectItem value="Professional">Professional</SelectItem>
                  <SelectItem value="Native">Native</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLanguage(false)}>
              Cancel
            </Button>
            <Button onClick={addNewLanguage}>Add Language</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Taxonomy Term Dialog */}
      <Dialog open={showAddTaxonomy} onOpenChange={setShowAddTaxonomy}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New {addTaxonomyType.replace('-', ' ')}</DialogTitle>
            <DialogDescription>
              Add a new term to the {addTaxonomyType.replace('-', ' ')} taxonomy
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="taxonomy-name">Name</Label>
            <Input
              id="taxonomy-name"
              value={newTaxonomyName}
              onChange={(e) => setNewTaxonomyName(e.target.value)}
              placeholder={`e.g. ${addTaxonomyType === 'technical-skills' ? 'JavaScript' : addTaxonomyType === 'tools' ? 'VS Code' : 'Communication'}`}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTaxonomy(false)}>
              Cancel
            </Button>
            <Button onClick={addNewTaxonomyTerm}>Add Term</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
